'use client'

import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Typography } from '@/ui/atoms/typography/Typography'
import { LogIn } from 'lucide-react'
import { useAccount } from 'wagmi'
import { useCardAuth } from '../hooks/useCardAuth'
import { useCardEligibility } from '../hooks/useCardEligibility'
import { CardEligibilityResult } from '../types/CardState'

interface CardSessionExpiredProps {
  onReauthenticated: (result: CardEligibilityResult) => void
}

export function CardSessionExpired({ onReauthenticated }: CardSessionExpiredProps) {
  const { address } = useAccount()
  const { clearTokens } = useCardAuth()
  const { checkEligibility, isLoading, error, reset } = useCardEligibility()

  const handleSignIn = async () => {
    if (!address) return

    reset()
    await clearTokens({ clearServerSession: false })

    try {
      const result = await checkEligibility(undefined)
      onReauthenticated(result)
    } catch {
      // Errors are surfaced via the hook error states
    }
  }

  return (
    <div className="flex items-center justify-center py-16">
      <Panel.Wrapper className="w-full max-w-md">
        <Panel.Content className="flex flex-col items-center gap-6 p-8 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <LogIn className="h-7 w-7 text-primary" />
          </div>

          <div>
            <Typography variant="h3" gradient>
              Session Expired
            </Typography>
            <Typography variant="p" className="mt-2 text-muted-foreground">
              Your session has expired. Sign in to continue using your card.
            </Typography>
          </div>

          <Button onClick={handleSignIn} disabled={isLoading || !address} className="min-w-[200px]">
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>

          {error && (
            <div className="w-full rounded-lg border border-red-500/20 bg-red-500/10 p-4">
              <Typography variant="p" className="text-red-400 text-sm">
                {error.message?.includes('User rejected') || error.message?.includes('rejected')
                  ? 'Signature was cancelled. Please try again.'
                  : error.message || 'Failed to sign in. Please try again.'}
              </Typography>
            </div>
          )}
        </Panel.Content>
      </Panel.Wrapper>
    </div>
  )
}
